import { useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  Paperclip, 
  Download, 
  Search, 
  Folder, 
  FolderOpen, 
  File, 
  FileText, 
  FileImage, 
  FileSpreadsheet,
  Archive,
  ChevronDown,
  ChevronRight,
  Plus
} from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface AttachmentFile {
  id: string;
  name: string;
  type: string;
  size: string;
  uploadDate: string;
  url: string;
  folder: string;
}

interface AttachmentsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Quando informado, mostra os anexos dos RDOs desta obra */
  obraId?: string;
}

// Anexos reais: payload dos RDOs, agrupados pela data do RDO
async function loadRealAttachments(obraId?: string): Promise<AttachmentFile[]> {
  const { listRDOs, getRDOs } = await import("@/services/rdoStorageSupabase");
  const rdos = obraId ? await listRDOs(obraId) : await getRDOs();
  const files: AttachmentFile[] = [];
  for (const rdo of rdos) {
    const folder = `RDO ${rdo.date.split("-").reverse().join("/")}`;
    (rdo.attachments ?? []).forEach((a, i) => {
      files.push({
        id: a.id ?? `${rdo.id}-${i}`,
        name: a.name ?? "Anexo",
        type: a.type ?? (a.name?.split(".").pop() ?? ""),
        size: a.size ?? "",
        uploadDate: rdo.date,
        url: a.url ?? "",
        folder,
      });
    });
  }
  return files;
}

const getFileIcon = (type: string) => {
  switch (type.toLowerCase()) {
    case 'pdf':
      return FileText;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
      return FileImage;
    case 'xlsx':
    case 'xls':
    case 'csv':
      return FileSpreadsheet;
    case 'zip':
    case 'rar':
    case '7z':
      return Archive;
    default:
      return File;
  }
};

const getFileTypeColor = (type: string) => {
  switch (type.toLowerCase()) {
    case 'pdf':
      return 'bg-red-100 text-red-700';
    case 'xlsx':
    case 'xls':
    case 'csv':
      return 'bg-green-100 text-green-700';
    case 'docx':
    case 'doc':
      return 'bg-blue-100 text-blue-700';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
      return 'bg-purple-100 text-purple-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

export function AttachmentsModal({ open, onOpenChange, obraId }: AttachmentsModalProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<string>("");
  const [fileDescription, setFileDescription] = useState<string>("");

  // Anexos reais dos RDOs, carregados ao abrir
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  useEffect(() => {
    if (!open) return;
    let active = true;
    loadRealAttachments(obraId)
      .then((files) => {
        if (!active) return;
        setAttachments(files);
        // Expande a pasta mais recente por padrão
        if (files.length > 0) setExpandedFolders(new Set([files[0].folder]));
      })
      .catch((e) => console.error("AttachmentsModal:", e));
    return () => { active = false; };
  }, [open, obraId]);

  // Filtrar arquivos por termo de busca
  const filteredAttachments = useMemo(() => {
    if (!searchTerm) return attachments;
    const term = searchTerm.toLowerCase();
    return attachments.filter(file =>
      file.name.toLowerCase().includes(term) ||
      file.folder.toLowerCase().includes(term) ||
      file.type.toLowerCase().includes(term)
    );
  }, [attachments, searchTerm]);

  // Obter lista única de pastas
  const folders = Array.from(new Set(attachments.map(file => file.folder))).sort();
  
  // Agrupar arquivos por pasta
  const filesByFolder = useMemo(() => {
    const groups: Record<string, AttachmentFile[]> = {};
    filteredAttachments.forEach(file => {
      if (!groups[file.folder]) {
        groups[file.folder] = [];
      }
      groups[file.folder].push(file);
    });
    return groups;
  }, [filteredAttachments]);

  const toggleFolder = (folderName: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderName)) {
      newExpanded.delete(folderName);
    } else {
      newExpanded.add(folderName);
    }
    setExpandedFolders(newExpanded);
  };

  const handleDownload = (file: AttachmentFile) => {
    // Simular download do arquivo
    toast({
      title: "Download iniciado",
      description: `Baixando ${file.name}...`,
    });
    
    // Em produção, você faria o download real do arquivo
    console.log('Downloading file:', file);
  };

  const handleAddFile = () => {
    setShowUploadModal(true);
    setSelectedFile(null);
    setSelectedFolder("");
    setFileDescription("");
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedFolder) {
      toast({
        title: "Erro",
        description: "Selecione um arquivo e uma pasta.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Simular upload - em produção, você faria o upload real
      toast({
        title: "Upload realizado",
        description: `Arquivo ${selectedFile.name} adicionado à pasta ${selectedFolder}`,
      });
      
      // Fechar modal e limpar campos
      setShowUploadModal(false);
      setSelectedFile(null);
      setSelectedFolder("");
      setFileDescription("");
      
      // Em produção, você atualizaria a lista de arquivos
      
    } catch (error) {
      toast({
        title: "Erro no upload",
        description: "Ocorreu um erro ao fazer upload do arquivo.",
        variant: "destructive",
      });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFolderStats = (folderName: string) => {
    const files = filesByFolder[folderName] || [];
    const totalSize = files.reduce((acc, file) => {
      const sizeInMB = parseFloat(file.size.replace(/[^\d.]/g, ''));
      return acc + sizeInMB;
    }, 0);
    
    return {
      count: files.length,
      size: totalSize.toFixed(1) + ' MB'
    };
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[95vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="w-5 h-5" />
              Anexos da Obra
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button onClick={handleAddFile} size="sm" className="gap-2">
                <Plus className="w-4 h-4" />
                Adicionar Arquivo
              </Button>
              <div className="text-sm text-muted-foreground">
                Total: {filteredAttachments.length} arquivos
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 flex flex-col gap-4 overflow-hidden">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Buscar arquivos ou pastas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Files List */}
          <div className="flex-1 overflow-auto">
            {Object.keys(filesByFolder).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Paperclip className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum arquivo encontrado</p>
              </div>
            ) : (
              <div className="space-y-2">
                {Object.entries(filesByFolder)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([folderName, files]) => {
                    const isExpanded = expandedFolders.has(folderName);
                    const stats = getFolderStats(folderName);
                    const FolderIcon = isExpanded ? FolderOpen : Folder;
                    const ChevronIcon = isExpanded ? ChevronDown : ChevronRight;
                    
                    return (
                      <Collapsible key={folderName} open={isExpanded} onOpenChange={() => toggleFolder(folderName)}>
                        <CollapsibleTrigger asChild>
                          <div className="flex items-center gap-2 p-3 bg-muted/50 hover:bg-muted rounded-lg cursor-pointer transition-colors">
                            <ChevronIcon className="w-4 h-4 text-muted-foreground" />
                            <FolderIcon className="w-5 h-5 text-blue-600" />
                            <span className="font-medium text-foreground">{folderName}</span>
                            <div className="flex items-center gap-2 ml-auto text-sm text-muted-foreground">
                              <span>{stats.count} arquivos</span>
                              <span>•</span>
                              <span>{stats.size}</span>
                            </div>
                          </div>
                        </CollapsibleTrigger>
                        
                        <CollapsibleContent>
                          <div className="ml-6 mt-2 space-y-1">
                            {files
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((file) => {
                                const FileIcon = getFileIcon(file.type);
                                const typeColorClass = getFileTypeColor(file.type);
                                
                                return (
                                  <div
                                    key={file.id}
                                    className="flex items-center gap-3 p-3 hover:bg-muted/30 rounded-lg transition-colors group"
                                  >
                                    <FileIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                    
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-foreground truncate">
                                          {file.name}
                                        </span>
                                        <Badge variant="secondary" className={`text-xs ${typeColorClass}`}>
                                          {file.type.toUpperCase()}
                                        </Badge>
                                      </div>
                                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                                        <span>{file.size}</span>
                                        <span>•</span>
                                        <span>{new Date(file.uploadDate).toLocaleDateString('pt-BR')}</span>
                                      </div>
                                    </div>
                                    
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDownload(file)}
                                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <Download className="w-4 h-4" />
                                    </Button>
                                  </div>
                                );
                              })}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Modal de Upload */}
    <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Adicionar Arquivo
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Seleção de Arquivo */}
          <div className="space-y-2">
            <Label htmlFor="file-upload">Arquivo</Label>
            <Input
              id="file-upload"
              type="file"
              onChange={handleFileSelect}
              className="cursor-pointer"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.zip,.rar"
            />
            {selectedFile && (
              <div className="text-sm text-muted-foreground">
                <span className="font-medium">{selectedFile.name}</span>
                <span className="ml-2">({formatFileSize(selectedFile.size)})</span>
              </div>
            )}
          </div>

          {/* Seleção de Pasta */}
          <div className="space-y-2">
            <Label htmlFor="folder-select">Pasta</Label>
            <Select value={selectedFolder} onValueChange={setSelectedFolder}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma pasta" />
              </SelectTrigger>
              <SelectContent>
                {folders.map((folder) => (
                  <SelectItem key={folder} value={folder}>
                    {folder}
                  </SelectItem>
                ))}
                <SelectItem value="nova-pasta">+ Nova Pasta</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Descrição (Opcional) */}
          <div className="space-y-2">
            <Label htmlFor="description">Descrição (opcional)</Label>
            <Textarea
              id="description"
              placeholder="Adicione uma descrição para o arquivo..."
              value={fileDescription}
              onChange={(e) => setFileDescription(e.target.value)}
              rows={3}
            />
          </div>

          {/* Botões */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowUploadModal(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || !selectedFolder}
            >
              Fazer Upload
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}